import {
  Box,
  TabList,
  Tab,
  Tabs,
  TabPanels,
  TabPanel,
  IconButton,
  Text,
  Heading,
  Flex,
} from '@chakra-ui/react';
import { AiAgentContext, useAgentSession } from './useAgentSession';
import { ChatContainer } from './chat/ChatContainer';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect, useMemo, useState } from 'react';
import { useSignalSubscriptionEvents } from '../shared/hooks/useSignalSubscriptionEvents';
import { useActiveSession } from '@texas/hooks/useSession';
import { aiEvents } from './events';
import { defaultIconSize, Icons } from '../shared/Icons';
import { NewChat } from './NewChat';
import { fadeInBottomAnimation } from '@texas/resources/animations/animations';
import { useTranslation } from 'react-i18next';
import {
  convertToEnum,
  ensureEnumNumber,
} from '@texas/utils/helpers/enumHelpers';
import React from 'react';
import { PromptInput } from './prompt/PromptInput';
import {
  chatApi,
  ChatThread,
  ThreadCategory,
} from '@texas/api/endpoints/chat/chatApi';
import { useResizeableBox } from '../shared/hooks/useResizeableBox';
import { FeatureGuard } from '../shared/featureGuard/FeatureGuard';
import { useLocalStorage } from '@texas/hooks/useLocalStorage';

const sidebarWidth = '60px';
const sidebarExpandedWidth = '240px';
const tabListItemHeight = '22px';

export function TexasAgent({ onClose }: { onClose?: () => void }) {
  const { t } = useTranslation();
  const { boxRef, width, resizableComponent, isDragging } = useResizeableBox(
    540,
    300,
    700,
  );
  const {
    data: sessions,
    refetch: refetchSessions,
    set: setSessions,
  } = useApiResource(chatApi.getSessions);

  const [tabIndex, setTabIndex] = useState(0);
  const [toggleSidebar, setToggleSidebar] = useLocalStorage(
    'agentSidebarOpen',
    true,
  );
  const isSidebarOpen = onClose ? true : toggleSidebar;

  useEffect(() => {
    refetchSessions();
  }, [refetchSessions]);

  useSignalSubscriptionEvents({
    triggerOnEvent: () => {
      refetchSessions();
    },
    signalEvents: [aiEvents.newThreadCreated],
  });

  useEffect(() => {
    const unsub = aiEvents.newThreadCreatedWithPrompt.subscribe((data) => {
      setSessions((x) => [...data, ...(x ?? [])]);

      setTabIndex(1);
    });

    return unsub;
  }, [setSessions]);

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };

  const categorisedSessions = useMemo(() => {
    const uniqueGroups: Record<ThreadCategory, ChatThread[]> = {
      [ThreadCategory.Agent]: [],
      [ThreadCategory.Report]: [],
    };

    sessions?.forEach((session) => {
      uniqueGroups[ensureEnumNumber(ThreadCategory, session.category)].push(
        session,
      );
    });

    return uniqueGroups;
  }, [sessions]);

  return (
    <FeatureGuard hideBadge={true}>
      <Tabs
        ref={boxRef}
        display="flex"
        zIndex={4}
        height="full"
        variant="unstyled-opacity"
        transition={isDragging ? 'none' : '200ms width ease'}
        isLazy={true}
        index={tabIndex}
        pos="relative"
        w={isSidebarOpen ? `${width}px` : sidebarWidth}
        onChange={handleTabsChange}
        onClick={() => {
          if (!isSidebarOpen) {
            setToggleSidebar(true);
          }
        }}
      >
        <Box h="full" pos="relative" w={sidebarWidth} role="group">
          <TabList
            overflowX="hidden"
            overflowY="hidden"
            zIndex={1}
            flexDir="column"
            padding={4}
            pt={12}
            transition="200ms width ease"
            width={sidebarWidth}
            pos="relative"
            background="linear-gradient(360deg, #434343, #222222)"
            _light={{
              background: 'linear-gradient(360deg, white, #fbfbfb)',
            }}
            gap={2}
            h="full"
            alignItems="start"
            _hover={{
              width: sidebarExpandedWidth,
              boxShadow: '2xl',
              overflowY: 'auto',
            }}
          >
            <IconButton
              aria-label="Toggle sidebar"
              variant="ghost"
              minW="unset"
              p="4px"
              mb={4}
              icon={
                isSidebarOpen ? (
                  <Icons.ArrowLeft boxSize={defaultIconSize} />
                ) : (
                  <Icons.ArrowRight boxSize={defaultIconSize} />
                )
              }
              size="sm"
              onClick={() => {
                if (onClose) {
                  onClose();
                  return;
                }

                setToggleSidebar(!toggleSidebar);
              }}
            />

            <Tab
              display="flex"
              gap={2}
              textAlign="start"
              w="fit-content"
              padding="6px"
              height={tabListItemHeight}
              alignItems="center"
            >
              <Icons.Plus color="texas.sand.100" />
              <Heading
                whiteSpace="nowrap"
                display="none"
                _groupHover={{ display: 'block' }}
                fontSize="md"
              >
                {t('chat.new')}
              </Heading>
            </Tab>
            {Object.keys(categorisedSessions).map((x) => {
              const e = convertToEnum(ThreadCategory, x);
              const key = e as unknown as keyof typeof ThreadCategory;
              const threads =
                categorisedSessions[ensureEnumNumber(ThreadCategory, e)];

              if (threads.length === 0) {
                return null;
              }

              return (
                <React.Fragment key={x}>
                  <Heading
                    fontSize="sm"
                    pt={6}
                    px="6px"
                    opacity={0.5}
                    whiteSpace="nowrap"
                    visibility="hidden"
                    _groupHover={{ visibility: 'visible' }}
                  >
                    {t(`chat.types.${key}.title`)}
                  </Heading>
                  {threads.map((s) => (
                    <Tab
                      key={s.threadId}
                      px="6px"
                      height={tabListItemHeight}
                      display="flex"
                      gap={2}
                      textAlign="start"
                      w="full"
                    >
                      <Icons.Chat
                        mr="auto"
                        display="block"
                        _groupHover={{ display: 'none' }}
                      />
                      <Text
                        flexGrow={1}
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        overflow="hidden"
                        display="none"
                        _groupHover={{ display: 'block' }}
                        fontSize="sm"
                      >
                        {s.title}
                      </Text>
                    </Tab>
                  ))}
                </React.Fragment>
              );
            })}
          </TabList>
        </Box>

        {isSidebarOpen && (
          <TabPanels
            animation={fadeInBottomAnimation()}
            border="1px solid"
            borderColor="texas.bg.800"
            _light={{ borderColor: 'gray.10' }}
            minW="0"
          >
            <TabPanel p={0} h="full">
              <NewChat />
            </TabPanel>
            {Object.keys(categorisedSessions).map((x) => {
              const e = convertToEnum(ThreadCategory, x);
              const threads =
                categorisedSessions[ensureEnumNumber(ThreadCategory, e)];

              if (threads.length === 0) {
                return null;
              }

              return threads.map((x) => {
                return (
                  <TabPanel key={x.threadId} p={0} h="full">
                    <Inner threadId={x.threadId} />
                  </TabPanel>
                );
              });
            })}
          </TabPanels>
        )}
        {resizableComponent}
      </Tabs>
    </FeatureGuard>
  );
}

interface InnerProps {
  threadId: number;
}

function Inner(props: InnerProps) {
  const agentSession = useAgentSession({
    threadId: props.threadId,
    poll: true,
  });
  const session = useActiveSession();

  return (
    <AiAgentContext.Provider value={agentSession}>
      <Box bg="texas.bg.950" _light={{ bg: 'white' }} h="full">
        <Flex
          transition="400ms width ease"
          flexDir="column"
          maxH="calc(100dvh - 82px)"
          overflow="auto"
          w="full"
          h="full"
          sx={{
            maskImage: 'linear-gradient(to top, #ffffff 95%, transparent)',
            maskClip: 'no-clip',
          }}
        >
          <Box p={8} pb="calc(100dvh - 400px)">
            <ChatContainer scrollOnMessages={true} />
          </Box>

          <PromptInput
            multipleFilesSupport={false}
            onSubmit={(msg, files, rawText) => {
              agentSession.prompt(session.currentUser.id, msg, files, rawText);
            }}
          />
        </Flex>
      </Box>
    </AiAgentContext.Provider>
  );
}
